import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Printer,
  FileDown,
  CheckCircle2,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import {
  getPackingList,
  upsertPackingList,
  lookupPackingInfo,
  getAppSettings,
  getPackingListPdfBlob,
} from "@/lib/api";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()}`;
};

const blankItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  customer_number: "",
  customer_name: "",
  area: "",
  color: "",
  abat: "",
  fx: "",
  sl: "",
  p: "",
  pd: "",
  plis: "",
  lateral: "",
  frame: "",
  notes: "",
});

const QTY_COLS = [
  { key: "abat", label: "Abat", title: "Abatible / side-opening door" },
  { key: "fx", label: "Fx", title: "Fixed screen" },
  { key: "sl", label: "Sl", title: "Sliding screen" },
  { key: "p", label: "P", title: "Pulldown window" },
  { key: "pd", label: "PD", title: "Pulldown door" },
  { key: "plis", label: "Plis", title: "Pleated screen" },
  { key: "lateral", label: "Lat.", title: "Lateral / side rails" },
  { key: "frame", label: "Frame", title: "Frame / trim" },
];

const cnDigits = (s) => (s || "").replace(/[^0-9]/gi, "");

const PackingListDetail = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const dirtyRef = useRef(false);

  useEffect(() => {
    Promise.all([getPackingList(date), getAppSettings()])
      .then(([p, s]) => {
        setPlan(p);
        setSettings(s || {});
      })
      .catch(() => toast.error("Failed to load packing list"))
      .finally(() => setLoading(false));
  }, [date]);

  const setItems = (updater) => {
    dirtyRef.current = true;
    setPlan((prev) => ({ ...prev, items: updater(prev.items || []) }));
  };

  const updateItem = (id, patch) => {
    setItems((items) => items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    setItems((items) => items.filter((it) => it.id !== id));
  };

  const addItem = () => {
    setItems((items) => [...items, blankItem()]);
  };

  // Type a customer number → fill name, area, and per-type counts from latest quote
  const handleNumberBlur = async (item) => {
    const raw = (item.customer_number || "").trim();
    if (!raw) return;
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) {
      updateItem(item.id, { customer_number: "" });
      return;
    }
    const label = `MN-${digits.padStart(3, "0")}`;
    try {
      const info = await lookupPackingInfo(label);
      const next = { customer_number: info.customer_number };
      if (!item.customer_name) next.customer_name = info.customer_name;
      if (!item.area && info.area) next.area = info.area;
      // Only fill counts if the row hasn't been touched yet — never clobber
      // a number the user typed manually.
      ["fx", "sl", "p", "pd", "plis", "abat"].forEach((k) => {
        if (!item[k] && info[k]) next[k] = info[k];
      });
      updateItem(item.id, next);
      toast.success(`Loaded ${info.customer_number} ${info.customer_name}`);
    } catch {
      updateItem(item.id, { customer_number: label });
      toast.error(`No customer found for "${label}"`);
    }
  };

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const fresh = await upsertPackingList(date, {
        items: plan.items || [],
        notes: plan.notes || "",
      });
      setPlan(fresh);
      dirtyRef.current = false;
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (dirtyRef.current) {
      try {
        await save();
      } catch {
        toast.error("Save first, then try the PDF");
        return;
      }
    }
    try {
      const blob = await getPackingListPdfBlob(date);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `packing_list_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const totalRows = useMemo(() => (plan?.items || []).length, [plan]);

  if (loading) {
    return (
      <div className="p-10 font-mono text-xs text-zinc-400 uppercase tracking-[0.18em]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="packing-list-page">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          [data-testid^="packing-item-"] {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          [data-testid="packing-table"] {
            font-size: 9pt;
            table-layout: fixed;
            width: 100% !important;
          }
          [data-testid="packing-table"] td,
          [data-testid="packing-table"] th {
            padding: 3px 4px !important;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          [data-testid="packing-table"] input,
          [data-testid="packing-table"] textarea {
            border: none !important;
            padding: 0 !important;
            font-size: 9pt !important;
            background: transparent !important;
            width: 100% !important;
            resize: none !important;
            overflow: visible !important;
          }
          [data-testid="packing-table"] thead { display: table-header-group; }
          [data-testid="packing-table-wrap"] {
            overflow: visible !important;
            border: none !important;
          }
          /* Hide the trash column */
          [data-testid="packing-table"] td:last-child,
          [data-testid="packing-table"] th:last-child { display: none; }
          [data-testid="packing-table"],
          [data-testid="packing-table"] th,
          [data-testid="packing-table"] td {
            border: 0.6pt solid #09090b !important;
          }
          [data-testid="packing-table"] thead tr {
            border-bottom: 1.2pt solid #09090b !important;
          }
        }
      `}</style>
      <div className="p-4 md:p-6">
        <div className="max-w-[1600px] mx-auto">
          <Link
            to="/packing-lists"
            className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#09090b] font-mono text-xs uppercase tracking-[0.18em] transition-colors print:hidden"
            data-testid="back-to-packing"
          >
            <ArrowLeft size={12} /> All packing lists
          </Link>

          <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="label-xs">PACKING LIST</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none mt-1">
                {fmtDate(date)}
              </h1>
              <div className="font-mono text-[11px] text-zinc-500 mt-2">
                {totalRows} customer{totalRows === 1 ? "" : "s"}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 print:hidden">
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 bg-[#09090b] hover:bg-[#27272a] disabled:opacity-50 text-white px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
                data-testid="packing-save-btn"
              >
                {saving ? (
                  <>Saving…</>
                ) : dirtyRef.current ? (
                  <><Save size={12} /> Save</>
                ) : (
                  <><CheckCircle2 size={12} /> Saved</>
                )}
              </button>
              <button
                onClick={downloadPdf}
                className="inline-flex items-center gap-1.5 bg-white border border-[#09090b] hover:bg-zinc-100 px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
                data-testid="packing-pdf-btn"
              >
                <FileDown size={12} /> Save PDF
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 bg-white border border-[#09090b] hover:bg-zinc-100 px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
                data-testid="packing-print-btn"
              >
                <Printer size={12} /> Print
              </button>
            </div>
          </div>

          {/* Table */}
          <div
            className="mt-6 overflow-x-auto border border-zinc-700"
            data-testid="packing-table-wrap"
          >
            <table
              className="w-full text-sm border-collapse [&_th]:border-r [&_th]:border-zinc-700 [&_td]:border-r [&_td]:border-zinc-700 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0"
              data-testid="packing-table"
            >
              <colgroup>
                <col style={{ width: "7%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                {QTY_COLS.map((c) => (
                  <col key={c.key} style={{ width: "5%" }} />
                ))}
                <col style={{ width: "10%" }} />
                <col style={{ width: "1%" }} />
              </colgroup>
              <thead className="bg-[#f4f4f5] border-b-2 border-zinc-800">
                <tr>
                  <th className="text-left p-1.5 label-xs">No.</th>
                  <th className="text-left p-1.5 label-xs">Name</th>
                  <th className="text-left p-1.5 label-xs">Area</th>
                  <th className="text-left p-1.5 label-xs">Colour</th>
                  {QTY_COLS.map((c) => (
                    <th
                      key={c.key}
                      className="text-center p-1.5 label-xs"
                      title={c.title}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="text-left p-1.5 label-xs">Note</th>
                  <th className="text-left p-1.5 label-xs">·</th>
                </tr>
              </thead>
              <tbody>
                {(plan.items || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={4 + QTY_COLS.length + 2}
                      className="p-10 text-center text-zinc-400 font-mono text-xs uppercase tracking-[0.18em]"
                    >
                      No customers added — tap "+ Add row" below to start
                    </td>
                  </tr>
                ) : (
                  (plan.items || []).map((it) => (
                    <tr
                      key={it.id}
                      className="border-b border-zinc-700 align-top"
                      data-testid={`packing-item-${it.id}`}
                    >
                      <td className="p-0">
                        <div className="flex items-stretch h-full">
                          <span className="bg-zinc-100 border-r border-zinc-700 px-1 inline-flex items-center font-mono text-[9px] font-bold text-zinc-500 select-none">
                            MN-
                          </span>
                          <input
                            value={cnDigits(it.customer_number)}
                            onChange={(e) =>
                              updateItem(it.id, {
                                customer_number: e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 4),
                              })
                            }
                            onBlur={() => handleNumberBlur(it)}
                            placeholder="001"
                            inputMode="numeric"
                            className="flex-1 min-w-0 px-1 py-1.5 font-mono text-xs tabular-nums border-0 focus:outline-none bg-transparent"
                            data-testid={`packing-num-${it.id}`}
                          />
                        </div>
                      </td>
                      <td className="p-0">
                        <input
                          value={it.customer_name}
                          onChange={(e) =>
                            updateItem(it.id, { customer_name: e.target.value })
                          }
                          placeholder="Name"
                          className="w-full border-0 px-1 py-1.5 text-sm focus:outline-none focus:bg-zinc-50"
                        />
                      </td>
                      <td className="p-0">
                        <input
                          value={it.area}
                          onChange={(e) =>
                            updateItem(it.id, { area: e.target.value })
                          }
                          placeholder="Aloha Golf"
                          className="w-full border-0 px-1 py-1.5 text-xs focus:outline-none focus:bg-zinc-50"
                        />
                      </td>
                      <td className="p-0">
                        <input
                          value={it.color}
                          onChange={(e) =>
                            updateItem(it.id, { color: e.target.value })
                          }
                          placeholder="RAL 7016"
                          className="w-full border-0 px-1 py-1.5 text-xs focus:outline-none focus:bg-zinc-50"
                        />
                      </td>
                      {QTY_COLS.map((c) => (
                        <td className="p-0 text-center" key={c.key}>
                          <input
                            value={it[c.key]}
                            onChange={(e) =>
                              updateItem(it.id, { [c.key]: e.target.value })
                            }
                            className="w-full text-center border-0 px-0.5 py-1.5 font-mono text-xs focus:outline-none focus:bg-zinc-50"
                          />
                        </td>
                      ))}
                      <td className="p-0">
                        <input
                          value={it.notes}
                          onChange={(e) =>
                            updateItem(it.id, { notes: e.target.value })
                          }
                          placeholder=""
                          className="w-full border-0 px-1 py-1.5 text-xs focus:outline-none focus:bg-zinc-50"
                        />
                      </td>
                      <td className="p-0 text-center">
                        <button
                          onClick={() => removeItem(it.id)}
                          className="w-full h-full inline-flex items-center justify-center text-zinc-400 hover:text-red-600 print:hidden"
                          title="Remove row"
                          data-testid={`packing-remove-${it.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <button
            onClick={addItem}
            className="mt-3 inline-flex items-center gap-1.5 border border-zinc-300 hover:border-[#09090b] px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors print:hidden"
            data-testid="add-packing-item"
          >
            <Plus size={12} /> Add row
          </button>

          <div className="mt-8 print:mt-4">
            <div className="label-xs mb-1.5">Notes</div>
            <textarea
              value={plan.notes || ""}
              onChange={(e) => {
                dirtyRef.current = true;
                setPlan((prev) => ({ ...prev, notes: e.target.value }));
              }}
              placeholder="Pickup brackets · Order more 2390mm legs · Anything else…"
              rows={3}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-[#09090b] resize-none"
              data-testid="packing-notes"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackingListDetail;
